<template lang="pug">
    section.slots
        paginated-items(
            :scope="scope"
            :type="key"
            :items="paginatedSlots"
            :show-filters="showFilters"
            :filters="pagination.filters"
            :sortables="pagination.sortables"
            :sort="pagination.sort"
            :variables="pagination.variables"
            :all-columns="allColumns"
            :active-columns="activeColumns"
            :page-info="pagination.pageInfo"
            @click="openSlotModal"
            @change-sort="changeSort"
            @change-filter="changeFilter"
            @show-table-columns="changeTableColumns"
            @pagination-size-change="changePaginationSize"
            @pagination-current-change="changeCurrentPage"
        )
            template(slot="top")
                el-row.table-top(type="flex" justify="space-between")
                    .left
                        el-button(type="primary" size="mini" icon="el-icon-fa-plus fas" @click="openSlotModal")
                            | {{ $t('New slot') }}
                        //- Only offered when the shop is switched on too - without it there is
                        //- nothing to sell a ticket through.
                        el-button(
                            v-if="canSellTickets"
                            plain
                            size="mini"
                            type="success"
                            icon="el-icon-fa-ticket-alt fas"
                            @click="ticketDrawerVisible = true"
                        ) {{ $t('Sell tickets') }}
                    .right
                        el-button(
                            plain
                            size="mini"
                            type="primary"
                            icon="el-icon-fa-filter fas"
                            @click="toggleFilterVisiblity"
                            :disabled="disable.showFilters"
                        )
                            span(v-if="showFilters") {{ $t('Hide filters') }}
                            span(v-else) {{ $t('Show filters') }}
            template(v-slot:ticketProducts="{ item }")
                //- A product can cover some dates and not others, so this belongs per row rather
                //- than once above the list.
                span.no-tickets(v-if="!(item.ticketProducts || []).length") -
                //- The x takes the product back off this one date - the way back from "Sell
                //- tickets". The product stays for its other dates.
                el-tag.ticket-product(
                    v-for="product in item.ticketProducts"
                    :key="product.id"
                    size="mini"
                    closable
                    :type="product.status === 'enabled' ? 'success' : 'info'"
                    :title="$t('Edit this ticket product')"
                    @click.native.stop="openTicketProduct(product)"
                    @close="askDetachTicketProduct(item, product)"
                )
                    i.fas.el-icon-fa-ticket-alt
                    span {{ product.name }}
            template(v-slot:actions="{ item }")
                el-button.delete-button(
                    v-if="! item.deleted"
                    plain
                    circle
                    size="mini"
                    type="danger"
                    icon="el-icon-fa-trash-alt far"
                    @click.stop.prevent="deleteSlot(item)"
                )
                el-button.restore-button(
                    v-else
                    plain
                    circle
                    size="mini"
                    type="success"
                    icon="el-icon-fa-trash-restore fas"
                    @click.stop.prevent="restoreSlot(item)"
                )
                el-button.duplicate-button(
                    plain
                    circle
                    size="mini"
                    type="primary"
                    icon="el-icon-fa-copy far"
                    @click.stop.prevent="duplicateSlot(item)"
                )
        //- The same stepper the product list uses, seeded with this event so the merchant does not
        //- have to find it again. Embedded, so saving hands back here instead of navigating away.
        confirm-delete-dialog(
            ref="detachTicketDialog"
            :title="$t('Remove ticket from this date')"
            :confirm-label="$t('Remove')"
            @confirm="detachTicketProduct"
        )

        el-drawer.ticket-drawer(
            :title="$t('Sell tickets for this event')"
            :visible="ticketDrawerVisible"
            :before-close="() => ticketDrawerVisible = false"
            :append-to-body="true"
            :destroy-on-close="true"
            direction="rtl"
            size="820px"
            custom-class="full-height-drawer"
        )
            .drawer-body.no-tabs
                .block-content
                    NewProductStepper(
                        ref="ticketStepper"
                        :prefill="ticketPrefill"
                        @saved="ticketSaved"
                        @state="state => ticketStepper = state"
                    )
            .drawer-footer
                el-button(
                    round
                    size="mini"
                    :disabled="ticketStepper.step <= 1"
                    @click="$refs.ticketStepper.prev()"
                ) {{ $t('Previous') }}
                el-button(
                    v-if="ticketStepper.step < ticketStepper.lastStep"
                    round
                    size="mini"
                    type="success"
                    :disabled="! ticketStepper.canAdvance"
                    @click="$refs.ticketStepper.next()"
                ) {{ $t('Next') }}
                el-button(
                    v-else
                    round
                    size="mini"
                    type="primary"
                    icon="el-icon-fa-check fas"
                    :loading="ticketStepper.saving"
                    @click="$refs.ticketStepper.submit($event, 'close')"
                ) {{ $t('Save and close') }}
        el-dialog(
            :title="modalTitle"
            :visible.sync="modalVisibility"
            :close-on-click-modal="false"
            top="10vh"
            width="580px"
            @closed="resetModal"
        )
            el-form.slot-modal-form(
                :model="modal"
                label-position="top"
                :rules="rules"
                ref="modal"
                v-loading="modalLoading"
                @submit.prevent.native="submitModalForm"
            )
                //- Top labels, like every other form in the admin (globals.sass enforces them with
                //- an ID selector), so Starts, Repeat, Comment and Link share one label system and
                //- one left edge. Start and end each carry their own date AND time, which is why
                //- there is no "ends on another day" toggle - an overnight slot is just a later
                //- end date.
                el-form-item(:label="$t('Starts')" prop="start_at")
                    .row-value
                        el-date-picker.date(
                            v-model="modalDate"
                            type="date"
                            :format="$t('yyyy-MM-dd')"
                            prefix-icon="far el-icon-fa-calendar-alt"
                            :picker-options="{ firstDayOfWeek: 1 }"
                            :clearable="false"
                        )
                        el-time-select.time(
                            v-model="modalStartTime"
                            :picker-options="{ start: '00:00', step: '00:15', end: '23:45' }"
                            :clearable="false"
                        )

                el-form-item(:label="$t('Ends')" prop="end_at")
                    .row-value
                        el-date-picker.date(
                            v-model="modalEndDate"
                            type="date"
                            :format="$t('yyyy-MM-dd')"
                            prefix-icon="far el-icon-fa-calendar-alt"
                            :picker-options="{ firstDayOfWeek: 1, disabledDate: disabledEndDate }"
                            :clearable="false"
                        )
                        el-time-select.time(
                            v-model="modalEndTime"
                            :picker-options="{ start: '00:00', step: '00:15', end: '23:45', minTime: sameDay ? modalStartTime : null }"
                            :clearable="false"
                        )

                //- Capacity for this one slot. Empty inherits the event's "seats per slot", which
                //- is what every slot did before this field existed.
                el-form-item(:label="$t('Seats')")
                    .row-value
                        //- Left empty the slot inherits the event's "seats per slot"; the
                        //- placeholder shows that inherited value so the merchant can see what
                        //- empty actually means here rather than having to remember it.
                        el-input.seats(
                            v-model="modalMemberLimit"
                            type="number"
                            min="1"
                            size="small"
                            :placeholder="eventSeatDefaultPlaceholder"
                        )

                template(v-if="modalType === 'new'")
                    el-form-item(:label="$t('Repeat')")
                        el-select.is-full-width(v-model="repeat.pattern")
                            el-option(
                                v-for="option in repeatPatterns"
                                :key="option.value"
                                :label="$t(option.label)"
                                :value="option.value"
                            )

                    //- "Repeat every 2 weeks" collapses the calendar's separate Frequency and
                    //- Every rows into one - the unit dropdown already says what the frequency is,
                    //- so a second control for it only repeats itself and costs a row. The weekday
                    //- strip and the monthly mode switch then ride along in the same row's free
                    //- space rather than each claiming a row of their own.
                    template(v-if="repeat.pattern === 'custom'")
                        el-form-item(:label="$t('Repeat every')")
                            .row-value
                                el-input-number.interval(
                                    v-model="repeat.customInterval"
                                    :min="1"
                                    :max="52"
                                    :step="1"
                                    step-strictly
                                    size="small"
                                    controls-position="right"
                                )
                                el-select.frequency(v-model="repeat.customUnit" size="small")
                                    el-option(
                                        v-for="unit in unitOptions"
                                        :key="unit.value"
                                        :label="$t(unit.label)"
                                        :value="unit.value"
                                    )
                                //- A checkbox group rather than seven buttons: it is the segmented
                                //- control the mode switch below uses, its shared borders render
                                //- evenly at fractional widths, and it binds the array directly.
                                el-checkbox-group.weekday-strip(
                                    v-if="repeat.customUnit === 'week'"
                                    v-model="repeat.weekdays"
                                    size="small"
                                )
                                    el-checkbox-button(
                                        v-for="day in weekdayOptions"
                                        :key="day.value"
                                        :label="day.value"
                                    ) {{ $t(day.label) }}
                                //- The calendar's two monthly modes: fixed days of the month, or a
                                //- weekday by position ("first Monday"). The second cannot be
                                //- expressed as days because the date moves every month.
                                el-radio-group.month-mode(
                                    v-else-if="repeat.customUnit === 'month'"
                                    v-model="repeat.monthMode"
                                    size="small"
                                )
                                    el-radio-button(label="days") {{ $t('By date') }}
                                    el-radio-button(label="ordinal") {{ $t('By weekday') }}

                        template(v-if="repeat.customUnit === 'month'")
                            el-form-item.monthdays(v-if="repeat.monthMode === 'days'")
                                .month-grid
                                    button.month-day(
                                        v-for="day in 31"
                                        :key="day"
                                        type="button"
                                        :class="{ 'is-active': repeat.monthdays.includes(day) }"
                                        @click="toggleMonthday(day)"
                                    ) {{ day }}

                            el-form-item(v-else)
                                .row-value
                                    el-select.ordinal(v-model="repeat.ordinal" size="small")
                                        el-option(
                                            v-for="position in ordinalOptions"
                                            :key="position.value"
                                            :label="$t(position.label)"
                                            :value="position.value"
                                        )
                                    el-select.ordinal-weekday(v-model="repeat.ordinalWeekday" size="small")
                                        el-option(
                                            v-for="day in weekdayOptions"
                                            :key="day.value"
                                            :label="$t(day.name)"
                                            :value="day.value"
                                        )

                    template(v-if="repeat.pattern !== 'none'")
                        el-form-item(:label="$t('End repeat')")
                            .row-value
                                el-select.end-mode(v-model="repeat.endMode")
                                    el-option(:label="$t('After')" value="count")
                                    el-option(:label="$t('On date')" value="until")
                                el-input-number.interval(
                                    v-if="repeat.endMode === 'count'"
                                    v-model="repeat.count"
                                    :min="2"
                                    :max="maxRepeatDates"
                                    :step="1"
                                    step-strictly
                                    controls-position="right"
                                )
                                span.unit(v-if="repeat.endMode === 'count'") {{ $t('dates') }}
                                el-date-picker.date(
                                    v-else
                                    v-model="repeat.until"
                                    type="date"
                                    :format="$t('yyyy-MM-dd')"
                                    prefix-icon="far el-icon-fa-calendar-alt"
                                    :picker-options="{ firstDayOfWeek: 1, disabledDate: disabledRepeatUntil }"
                                    :clearable="false"
                                )

                        el-form-item.repeat-summary(v-if="repeatWarning || repeatPreview")
                            .repeat-preview(:class="{ 'is-warning': !!repeatWarning }")
                                span {{ repeatWarning || repeatPreview }}
                el-form-item(:label="$t('Comment')" prop="comment")
                    quill-editor(v-model="modal.comment" :options="quillConfig")

                el-form-item.input-field(:label="$t('Link')" prop="hyperlink.type")
                    .link
                        el-radio-group(v-model="modal.hyperlink.type" size="mini")
                            el-radio-button(v-if="categories.length" name="type" label="post") {{ $t('Post') }}
                            el-radio-button(name="type" label="internal") {{ $t('Menu item') }}
                            el-radio-button(name="type" label="external") {{ $t('External URL') }}
                            el-radio-button(name="type" label="mailto") {{ $t('Email') }}
                            el-radio-button(name="type" label="tel") {{ $t('Phone') }}
                        transition(name="slide-vertical")
                            .post(v-if="modal.hyperlink.type === 'post'")
                                el-select.is-full-width(
                                    v-model="modalHyperlinkPage"
                                    :placeholder="$t('Find a post ...')"
                                    filterable
                                    remote
                                    clearable
                                    :remote-method="findPost"
                                    :loading="post.loading"
                                )
                                    el-option(
                                        v-for="option in post.items"
                                        :key="option.id"
                                        :label="option.title"
                                        :value="option.id"
                                    )
                                        PostOptionPreview(:post="option")
                            .internal(v-else-if="modal.hyperlink.type === 'internal'")
                                .flex-row
                                    Treeselect(
                                        v-model="modalHyperlinkMenuItem"
                                        :placeholder="$t('Select a menu item ...')"
                                        :multiple="false"
                                        :options="getPageOptions('treeselect')"
                                        :default-expand-level="Infinity"
                                        :close-on-select="true"
                                        :open-on-focus="true"
                                        :searchable="true"
                                        :normalizer="node => ({ label: node.title, ...node })"
                                        :clearable="true"
                                        :always-open="false"
                                    )
                                        label(
                                            slot="option-label"
                                            slot-scope="{ node }"
                                            :class="{ optgroup: node.raw.optgroup }"
                                        ) {{ node.raw.title }}
                                        div(
                                            slot="value-label"
                                            slot-scope="{ node }"
                                            :class="{ optgroup: node.raw.optgroup }"
                                        ) {{ node.raw.title }}
                                    //- el-input(v-if="linkTextSetting" type="text" v-model="modal.hyperlink.name" :placeholder="$t('Text')")
                            .external(v-else)
                                .flex-row
                                    el-input(type="text" v-model="modal.hyperlink.href")
                                    //- el-input(type="text" v-model="modal.hyperlink.name" :placeholder="$t('Text')")
                                el-checkbox(v-if="modal.hyperlink.type === 'external'" v-model="hyperlinkTarget") {{ $t('Open link in new window/tab') }}
                                //- el-checkbox(v-if="modal.hyperlink.type === 'external'" v-model="modal.hyperlink.target") {{ $t('Open link in new window/tab') }}

            template(slot="footer")
                .modal-footer
                    .left
                    .right
                        el-button(
                            size="mini"
                            icon="el-icon-fa-times fas"
                            @click="closeModal"
                        ) {{ $t('Dismiss') }}
                        el-button(
                            :disabled="modalLoading || !!repeatWarning"
                            size="mini"
                            type="primary"
                            icon="el-icon-fa-check fas"
                            @click="submitModalForm"
                        ) {{ $t('Ok') }}
</template>

<i18n>
{
    "de": {
        "New slot": "Neuer Slot",
        "Starts": "Startet",
        "Seats": "Plätze",
        "unlimited": "unbegrenzt",
        "Ends": "Endet",
        "Repeat": "Wiederholen",
        "Does not repeat": "Wiederholt sich nicht",
        "Every day": "Täglich",
        "Every week": "Wöchentlich",
        "Every 2 weeks": "Alle 2 Wochen",
        "Every month": "Monatlich",
        "Custom": "Benutzerdefiniert",
        "Repeat every": "Wiederholt alle",
        "By date": "Nach Datum",
        "By weekday": "Nach Wochentag",
        "first": "am ersten",
        "second": "am zweiten",
        "third": "am dritten",
        "fourth": "am vierten",
        "last": "am letzten",
        "Monday": "Montag",
        "Tuesday": "Dienstag",
        "Wednesday": "Mittwoch",
        "Thursday": "Donnerstag",
        "Friday": "Freitag",
        "Saturday": "Samstag",
        "Sunday": "Sonntag",
        "day": "Tag",
        "days": "Tage",
        "week": "Woche",
        "weeks": "Wochen",
        "month": "Monat",
        "months": "Monate",
        "Tu": "Di",
        "We": "Mi",
        "Th": "Do",
        "Sa": "Sa",
        "Su": "So",
        "End repeat": "Wdh. endet",
        "After": "Nach",
        "On date": "Am",
        "dates": "Terminen",
        "Pick the date the series should run until.": "Bitte das Datum wählen, bis zu dem die Serie laufen soll.",
        "Pick at least one weekday.": "Bitte mindestens einen Wochentag wählen.",
        "No further dates fit before that end date.": "Vor diesem Enddatum liegt kein weiterer Termin.",
        "Pick at least one day of the month.": "Bitte mindestens einen Tag im Monat wählen.",
        "Stops at {count} dates. Choose an earlier end date for fewer.": "Stoppt bei {count} Terminen. Für weniger ein früheres Enddatum wählen.",
        "Creates {count} dates, last one on {date}": "Erzeugt {count} Termine, der letzte am {date}",
        "This date is part of a repeating series of {count} dates.": "Dieser Termin gehört zu einer Serie mit {count} Terminen.",
        "Delete date": "Termin löschen",
        "Delete all dates": "Alle Termine löschen",
        "Only this date": "Nur dieser Termin",
        "Move date": "Termin verschieben",
        "Move all dates": "Alle Termine verschieben",
        "Sell tickets": "Tickets verkaufen",
        "Remove ticket from this date": "Ticket von diesem Termin entfernen",
        "Remove": "Entfernen",
        "\"{product}\" will no longer admit to this date; the date takes free registrations again. The product and the tickets already sold are kept.": "„{product}“ berechtigt dann nicht mehr zu diesem Termin; der Termin nimmt wieder kostenlose Anmeldungen an. Das Produkt und bereits verkaufte Tickets bleiben erhalten.",
        "The ticket has been removed from this date.": "Das Ticket wurde von diesem Termin entfernt.",
        "The ticket could not be removed.": "Das Ticket konnte nicht entfernt werden.",
        "Edit this ticket product": "Dieses Ticket-Produkt bearbeiten",
        "Sell tickets for this event": "Tickets für diese Veranstaltung verkaufen",
        "Previous": "Zurück",
        "Next": "Weiter",
        "Save and close": "Speichern und schließen",
        "Edit slot": "Slot bearbeiten",
        "Start": "Start",
        "End": "Ende",
        "Comment": "Kommentar",
        "Link": "Link",
        "Show filters": "Filter anzeigen",
        "Hide filters": "Filter verbergen",
        "Target": "Ziel",
        "Select a menu item ...": "Menüpunkt auswählen ...",
        "Find a post ...": "Kategoriebeitrag suchen ...",
        "Email": "E-Mail",
        "Phone number": "Telefonnummer",
        "Post": "Beitrag",
        "Menu item": "Menüpunkt",
        "External URL": "Externe URL",
        "Phone": "Telefon",
        "Download": "Download",
        "Open link in new window/tab": "Link in einem neuen Fenster/Tab öffnen"
    },
    "en": {
        "\"{product}\" will no longer admit to this date; the date takes free registrations again. The product and the tickets already sold are kept.": "\"{product}\" will no longer admit to this date; the date takes free registrations again. The product and the tickets already sold are kept.",
        "Creates {count} dates, last one on {date}": "Creates {count} dates, last one on {date}",
        "Stops at {count} dates. Choose an earlier end date for fewer.": "Stops at {count} dates. Choose an earlier end date for fewer.",
        "This date is part of a repeating series of {count} dates.": "This date is part of a repeating series of {count} dates."
    }
}
</i18n>
<script>

import _ from 'lodash'
import Vue from 'vue'
import uniqid from 'uniqid'

import { compareAsc, compareDesc, addDays, addHours, addMonths, addWeeks, startOfHour, startOfWeek, getUnixTime, fromUnixTime } from 'date-fns'
import { timestamp2date } from '@/filters'
import { appFromUnixTime, appGetUnixTime } from '@/utils/timezone'

import NestedSet from '@/nested-set-lib'
import quillConfig from '@/quill/config'
import pinia from '@/pinia'
import { useAuthStore, useSetupStore, useEventManagerEventStore } from '@/stores'

import Treeselect from '@riophae/vue-treeselect'
import PaginatedItems from '@/components/PaginatedItems'
import PostOptionPreview from './Partials/PostOptionPreview'
import NewProductStepper from '@/extensions/shop/views/Product/NewProductStepper'
import ConfirmDeleteDialog from '@/components/ConfirmDeleteDialog'

/**
 * Tomorrow, on the hour, one hour long - what a new slot starts from.
 *
 * Seeded rather than blank: emptying a required field trips its rule on the way out of the modal,
 * and the message is still on screen when it is reopened with values. Both open paths overwrite
 * these, so the seed itself is never what the merchant sees.
 */
const defaultSlotWindow = () => {
    const start = startOfHour(addDays(new Date(), 1))

    return {
        start_at: appGetUnixTime(start),
        end_at: appGetUnixTime(addHours(start, 1)),
    }
}

export default {
    name: 'Slots',
    data() {
        quillConfig.modules.link.pages = this.getPageOptions
        return {
            scope: 'ex',
            key: 'evnt_event_slot',
            ticketDrawerVisible: false,
            // Repeat pattern for a NEW slot. Generates plain slots on save; not stored anywhere.
            repeat: {
                pattern: 'none',
                endMode: 'count',
                count: 4,
                until: null,
                // Only used by the 'custom' pattern; the shorthands are presets of these.
                customUnit: 'week',
                customInterval: 1,
                weekdays: [],
                monthMode: 'days',
                monthdays: [],
                ordinal: 1,
                ordinalWeekday: 1,
            },
            // Pushed up by the stepper so the drawer footer can drive it.
            ticketStepper: { step: 1, lastStep: 3, saving: false, loading: false, canAdvance: true },
            disable: {
                showFilters: false,
            },
            modalVisibility: false,
            modalLoading: false,
            modalType: 'new', // valid options: 'new' / fresh / 'existing'
            post: {
                loading: false,
                items: []
            },
            modal: {
                id: undefined,
                member_limit: null,
                comment: '',
                hyperlink: {
                    name: '',
                    href: '',
                    type: 'post',
                    target: '_self',
                    download: false,
                    menuItem: undefined,
                    page: undefined,
                },
                ...defaultSlotWindow(),
                // daterange: undefined,
                series_key: null,
                canceled_at: null,
                // TODO
                // address: {
                //     id: undefined,
                //     street: '',
                //     zipcode: '',
                //     city: '',
                //     country: '',
                // },
            },
            pagination: {
                filters: {},
                sortables: ['start_at', 'end_at'],
                sort: {
                    field: 'start_at',
                    direction: 'desc',
                },
                variables: {
                    paginationSizes: [5, 10, 50],
                },
                pageInfo: {
                    currentPage: 1,
                    lastPage: 1,
                    perPage: 10,
                    total: 0,
                }
            },
            quillConfig,
        }
    },
    components: {
        Treeselect,
        PaginatedItems,
        PostOptionPreview,
        NewProductStepper,
        ConfirmDeleteDialog,
    },
    watch: {
        slots(slots) {
            this.pagination.pageInfo.total = slots.length
            this.pagination.pageInfo.lastPage = Math.ceil(slots.length / this.pagination.pageInfo.perPage)
        },
        /**
         * Opening the weekly custom form with nothing ticked would generate nothing, so it starts
         * on the day the slot itself falls on - the same thing a calendar preselects.
         */
        'repeat.customUnit': {
            immediate: true,
            handler(unit) {
                if (!this.modal.start_at) return

                const start = appFromUnixTime(this.modal.start_at)

                if (unit === 'week' && !this.repeat.weekdays.length) {
                    this.repeat.weekdays = [start.getDay()]
                }

                if (unit === 'month' && !this.repeat.monthdays.length) {
                    this.repeat.monthdays = [start.getDate()]
                    this.repeat.ordinalWeekday = start.getDay()
                    // Which occurrence of that weekday the slot itself is - the 17th is the third
                    // Monday. Anything past the fourth is only reachable as "last".
                    this.repeat.ordinal = Math.min(Math.ceil(start.getDate() / 7), 4)
                }
            }
        },
        'repeat.pattern'(pattern) {
            if (pattern !== 'custom' || this.repeat.weekdays.length || !this.modal.start_at) return

            this.repeat.weekdays = [appFromUnixTime(this.modal.start_at).getDay()]
        }
    },
    computed: {
        categories() {
            return _.get(this.activeCms, 'config.template.categories') || [];
        },
        /**
         * Selling needs the shop as well as the event manager. Without both there is nothing to
         * take the money, and the backend refuses the product type anyway.
         */
        canSellTickets() {
            const enabled = this.setupStore.enabledExtensions || [];
            return enabled.includes('shop')
                && enabled.includes('event-manager')
                && !!_.get(this.form, 'id')
                && !String(_.get(this.form, 'id')).startsWith('new:')
                // With "Anmeldungen zulassen" off the event takes no attendees at all, so a ticket
                // to it could not admit anyone - the backend refuses it outright.
                && _.get(this.form, 'booking') === 'free';
        },
        /**
         * Seeds the stepper with this event and every date still to come, so the common case -
         * "sell the whole course" - needs no picking at all.
         */
        ticketPrefill() {
            const now = Math.floor(Date.now() / 1000);
            const upcoming = _.filter(
                _.get(this.form, 'slots') || [],
                slot => !slot.canceled_at && !slot.deleted && slot.end_at > now
            );

            return {
                type: 'event',
                name: _.get(this.form, 'name') || '',
                event: { id: _.get(this.form, 'id') },
                slots: upcoming.map(slot => ({ id: parseInt(slot.id) })),
                // The type watcher only fires on a change, and this drawer opens already set to
                // 'event' - so the § 312g default has to be seeded here too.
                returnable: false,
            };
        },
        slots() {
            let slots = _.get(this.form, 'slots', [])
            slots = _.cloneDeep(slots)

            //  apply filters
            slots = _.orderBy(slots, this.pagination.sort.field, this.pagination.sort.direction).filter(slot => {
                let match = true

                if (this.pagination.filters.id) {
                    const filter = this.pagination.filters.id
                    if (match && filter.from) {
                        match = slot.id >= filter.from
                    }
                    if (match && filter.to) {
                        match = slot.id <= filter.to
                    }
                }

                if (match && this.pagination.filters.start_at) {
                    const filter = this.pagination.filters.start_at
                    return slot.start_at >= filter.from
                        && slot.start_at <= filter.to
                }

                if (match && this.pagination.filters.end_at) {
                    const filter = this.pagination.filters.end_at
                    return slot.end_at >= filter.from
                        && slot.end_at <= filter.to
                }

                if (match && this.pagination.filters.comment) {
                    const filter = this.pagination.filters.comment
                    const search = _.toLower(filter.like)
                    match = _.toLower(slot.comment).indexOf(search) >= 0
                }

                return match
            })

            return slots
        },
        paginatedSlots() {
            let slots = this.slots

            const index = this.pagination.pageInfo.currentPage - 1
            const start = this.pagination.pageInfo.perPage * index
            const end = start + this.pagination.pageInfo.perPage

            slots = slots.slice(start, end)
            return slots
        },
        activeColumns() {
            let activeColumns = [];

            const activeColumnKeys = _.get(this.user.settings, `${this.scope}.pagination.${this.key}.activeColumnKeys`, [])
            const decided = _.map(activeColumnKeys, 'key')

            _.each(this.allColumns, column => {
                const allowed = _.find(activeColumnKeys, obj => column.key === obj.key);
                // A column added after the merchant last saved their column preferences has no
                // decision recorded against it, so its own default applies rather than "off" -
                // otherwise a new column is invisible to everyone who ever touched this list.
                const undecided = column.default === true && !_.includes(decided, column.key);

                if (allowed || undecided) activeColumns.push(column);
            });

            if (!activeColumns.length) {
                activeColumns = this.allColumns.filter(column => column.default === true)
            }

            return activeColumns
        },
        showFilters() {
            return _.get(this.user.settings, `${this.scope}.pagination.${this.key}.showFilters`) || false
        },
        allColumns() {
            const filterOptionsFromTo = [
                {
                    key: 'from',
                    placeholder: 'von',
                    position: 10,
                },
                {
                    key: 'to',
                    placeholder: 'bis',
                    position: 20,
                }
            ]

            const columns = [
                {
                    key: 'id',
                    name: 'ID',
                    value_path: 'id',
                    filter: {
                        type: 'NumberFromTo',
                        options: filterOptionsFromTo
                    },
                    default: false,
                },
                // {
                //     key: 'gender',
                //     name: 'Geschlecht',
                //     value_path: 'gender',
                //     filter: {
                //         type: 'Select',
                //         options: [
                //             {
                //                 key: 'M',
                //                 label: 'Mann',
                //                 placeholder: null,
                //                 position: 10,
                //             },
                //             {
                //                 key: 'F',
                //                 label: 'Frau',
                //                 placeholder: null,
                //                 position: 20,
                //             }
                //         ]
                //     },
                //     default: false,
                // },
                {
                    key: 'start_at',
                    name: 'Start',
                    value_path: 'start_at',
                    filter: {
                        type: 'DateTimeFromTo',
                        options: filterOptionsFromTo
                    },
                    default: true,
                },
                {
                    key: 'end_at',
                    name: 'Ende',
                    value_path: 'end_at',
                    filter: {
                        type: 'DateTimeFromTo',
                        options: filterOptionsFromTo
                    },
                    default: true,
                },
                {
                    key: 'ticketProducts',
                    name: 'Tickets',
                    value_path: 'ticketProducts',
                    default: true,
                },
                {
                    key: 'comment',
                    name: 'Kommentar',
                    value_path: 'comment',
                    filter: {
                        type: 'SingleInput',
                    },
                    default: false,
                },
            ]

            return columns
        },
        modalTitle() {
            let title = this.$t('New slot')
            if (this.modalType === 'existing') {
                title = this.$t('Edit slot')
            }
            return title
        },
        rules() {
            return {
                start_at: 'required',
                end_at: 'required',
            }
        },
        repeatPatterns() {
            return [
                { value: 'none', label: 'Does not repeat' },
                { value: 'daily', label: 'Every day' },
                { value: 'weekly', label: 'Every week' },
                { value: 'biweekly', label: 'Every 2 weeks' },
                { value: 'monthly', label: 'Every month' },
                { value: 'custom', label: 'Custom' },
            ]
        },
        /**
         * Monday first, matching the calendar pickers. Values are JS getDay(), where Sunday is 0.
         */
        weekdayOptions() {
            return [
                { value: 1, label: 'Mo', name: 'Monday' },
                { value: 2, label: 'Tu', name: 'Tuesday' },
                { value: 3, label: 'We', name: 'Wednesday' },
                { value: 4, label: 'Th', name: 'Thursday' },
                { value: 5, label: 'Fr', name: 'Friday' },
                { value: 6, label: 'Sa', name: 'Saturday' },
                { value: 0, label: 'Su', name: 'Sunday' },
            ]
        },
        /**
         * 1st to 4th plus "last" - there is no fifth option because not every month has a fifth
         * Monday, which is exactly what "last" is for.
         */
        ordinalOptions() {
            return [
                { value: 1, label: 'first' },
                { value: 2, label: 'second' },
                { value: 3, label: 'third' },
                { value: 4, label: 'fourth' },
                { value: 'last', label: 'last' },
            ]
        },
        /**
         * The unit dropdown, pluralised against the interval beside it, so it reads "every 1 week"
         * and "every 2 weeks" rather than "every 1 weeks".
         */
        unitOptions() {
            const plural = (this.repeat.customInterval || 1) > 1

            return [
                { value: 'day', label: plural ? 'days' : 'day' },
                { value: 'week', label: plural ? 'weeks' : 'week' },
                { value: 'month', label: plural ? 'months' : 'month' },
            ]
        },
        /**
         * Every shorthand is a preset of the custom form, so there is one generator rather than one
         * per pattern.
         */
        repeatRule() {
            const rules = {
                daily: { unit: 'day', interval: 1, weekdays: [], monthdays: [], ordinal: null },
                weekly: { unit: 'week', interval: 1, weekdays: [], monthdays: [], ordinal: null },
                biweekly: { unit: 'week', interval: 2, weekdays: [], monthdays: [], ordinal: null },
                monthly: { unit: 'month', interval: 1, weekdays: [], monthdays: [], ordinal: null },
                custom: {
                    unit: this.repeat.customUnit,
                    interval: Math.max(parseInt(this.repeat.customInterval) || 1, 1),
                    weekdays: this.repeat.customUnit === 'week' ? this.repeat.weekdays : [],
                    monthdays: this.repeat.customUnit === 'month' && this.repeat.monthMode === 'days'
                        ? this.repeat.monthdays
                        : [],
                    ordinal: this.repeat.customUnit === 'month' && this.repeat.monthMode === 'ordinal'
                        ? { position: this.repeat.ordinal, weekday: this.repeat.ordinalWeekday }
                        : null,
                },
            }

            return rules[this.repeat.pattern] || null
        },
        /**
         * A run longer than this is far more likely to be a mistyped end date than an intention,
         * and every one of these becomes a real row.
         */
        maxRepeatDates() {
            return 60
        },
        /**
         * Why nothing will be generated, when that is the case. An end date is the only way to get
         * here - the count field cannot be emptied.
         */
        repeatWarning() {
            if (this.repeat.pattern === 'none') return ''

            if (this.repeat.endMode === 'until' && !this.repeat.until) {
                return this.$t('Pick the date the series should run until.')
            }

            if (this.repeat.pattern === 'custom') {
                if (this.repeat.customUnit === 'week' && !this.repeat.weekdays.length) {
                    return this.$t('Pick at least one weekday.')
                }

                if (this.repeat.customUnit === 'month'
                    && this.repeat.monthMode === 'days'
                    && !this.repeat.monthdays.length) {
                    return this.$t('Pick at least one day of the month.')
                }
            }

            // A repeat that yields one date is the merchant asking for a series and getting a
            // single slot - an end date earlier than the second occurrence. Say so rather than
            // leaving the summary blank.
            if (this.buildRepeatDates().length < 2) {
                return this.$t('No further dates fit before that end date.')
            }

            if (this.buildRepeatDates().length >= this.maxRepeatDates) {
                return this.$t('Stops at {count} dates. Choose an earlier end date for fewer.', {
                    count: this.maxRepeatDates,
                })
            }

            return ''
        },
        /**
         * What the pattern will actually produce, so the merchant sees it before saving rather than
         * finding out afterwards.
         */
        repeatPreview() {
            const dates = this.buildRepeatDates()
            if (dates.length < 2) return ''

            return this.$t('Creates {count} dates, last one on {date}', {
                count: dates.length,
                date: timestamp2date(appGetUnixTime(dates[dates.length - 1].start), 'dd.MM.yyyy'),
            })
        },
        /**
         * The day the slot starts on. Moving it carries the whole slot, keeping its duration.
         */
        /**
         * Seats for this slot. Empty means "inherit the event default", which is how every slot
         * behaved before the field existed - so leaving it alone changes nothing.
         */
        modalMemberLimit: {
            get() {
                return this.modal.member_limit ? String(this.modal.member_limit) : ''
            },
            set(value) {
                const limit = parseInt(value, 10)

                this.$set(this.modal, 'member_limit', limit > 0 ? limit : null)
            }
        },
        /**
         * What an empty field means: the event's own per-slot limit, or unlimited when it has none.
         */
        eventSeatDefaultPlaceholder() {
            const limit = _.get(this.form, 'member_limit_per_slot')

            return limit ? String(limit) : this.$t('unlimited')
        },
        modalDate: {
            get() {
                return this.modal.start_at ? appFromUnixTime(this.modal.start_at) : null
            },
            set(value) {
                if (!value) return

                const current = this.modal.start_at ? appFromUnixTime(this.modal.start_at) : new Date()
                const next = new Date(value)
                next.setHours(current.getHours(), current.getMinutes(), 0, 0)

                this.shiftSlot(appGetUnixTime(next))
            }
        },
        /**
         * Start time. Moving it drags the end along so the duration is preserved, which is what a
         * calendar does and what stops the end silently landing before the start.
         */
        modalStartTime: {
            get() {
                return this.modal.start_at ? timestamp2date(this.modal.start_at, 'HH:mm') : null
            },
            set(value) {
                if (!value) return

                const [hours, minutes] = value.split(':').map(Number)
                const next = this.modal.start_at ? appFromUnixTime(this.modal.start_at) : new Date()
                next.setHours(hours, minutes, 0, 0)

                this.shiftSlot(appGetUnixTime(next))
            }
        },
        modalEndTime: {
            get() {
                return this.modal.end_at ? timestamp2date(this.modal.end_at, 'HH:mm') : null
            },
            set(value) {
                if (!value) return

                const [hours, minutes] = value.split(':').map(Number)
                const base = this.modal.end_at ? appFromUnixTime(this.modal.end_at) : appFromUnixTime(this.modal.start_at)
                base.setHours(hours, minutes, 0, 0)

                this.modal.end_at = appGetUnixTime(base)
            }
        },
        modalEndDate: {
            get() {
                return this.modal.end_at ? appFromUnixTime(this.modal.end_at) : null
            },
            set(value) {
                if (!value) return

                const current = appFromUnixTime(this.modal.end_at)
                const next = new Date(value)
                next.setHours(current.getHours(), current.getMinutes(), 0, 0)

                this.modal.end_at = appGetUnixTime(next)
            }
        },
        /**
         * Whether the slot begins and ends on the same day. Only used to decide whether the end
         * time may sit before the start time - across days it obviously may.
         */
        sameDay() {
            if (!this.modal.start_at || !this.modal.end_at) return true

            return timestamp2date(this.modal.start_at, 'yyyy-MM-dd')
                === timestamp2date(this.modal.end_at, 'yyyy-MM-dd')
        },
        modalStartAt: {
            get() {
                let startAt = this.modal.start_at
                if (startAt) startAt = appFromUnixTime(startAt)
                return startAt
            },
            set(value) {
                const startAt = value !== null ? appGetUnixTime(value) : null
                this.modal.start_at = startAt
            }
        },
        modalEndAt: {
            get() {
                let endAt = this.modal.end_at
                if (endAt) endAt = appFromUnixTime(endAt)
                return endAt
            },
            set(value) {
                const endAt = value !== null ? appGetUnixTime(value) : null
                this.modal.end_at = endAt
            }
        },
        modalHyperlinkPage: {
            get() {
                return _.get(this.modal, 'hyperlink.page.id')
            },
            set(id) {
                this.modal.hyperlink.page = { id }
            }
        },
        modalHyperlinkMenuItem: {
            get() {
                return _.get(this.modal, 'hyperlink.menuItem.id')
            },
            set(id) {
                this.modal.hyperlink.menuItem = { id }
            }
        },
        hyperlinkTarget: {
            get() {
                return !(this.modal.hyperlink.target == '_self')
            },
            set(value) {
                if (value) this.modal.hyperlink.target = "_blank"
                else this.modal.hyperlink.target = "_self"
            }
        },
        setupStore() {
            return useSetupStore(pinia);
        },
        eventStore() {
            return useEventManagerEventStore();
        },
        user() {
            return useAuthStore().user;
        },
        activeCms() {
            return useSetupStore().activeCms;
        },
        form() {
            return this.eventStore.form;
        },
        options() {
            return this.eventStore.options;
        },
    },
    methods: {
        /**
         * Move the slot to a new start, carrying the end with it so the duration never changes.
         */
        shiftSlot(startAt) {
            const duration = (this.modal.start_at && this.modal.end_at)
                ? this.modal.end_at - this.modal.start_at
                : 3600

            this.modal.start_at = startAt
            this.modal.end_at = startAt + duration
        },

        /**
         * The start/end pairs the current repeat pattern produces, the first being the slot itself.
         * Bounded so a stray "until" ten years out cannot generate thousands of rows.
         */
        buildRepeatDates() {
            const start = this.modal.start_at ? appFromUnixTime(this.modal.start_at) : null
            const rule = this.repeatRule

            if (!start || !rule) return []

            // An end date is the only way to ask for an unbounded run, so an empty one generates
            // nothing rather than silently running to the cap.
            if (this.repeat.endMode === 'until' && !this.repeat.until) return []

            const duration = (this.modal.end_at || this.modal.start_at + 3600) - this.modal.start_at

            const limit = this.repeat.endMode === 'count'
                ? Math.min(Math.max(parseInt(this.repeat.count) || 2, 2), this.maxRepeatDates)
                : this.maxRepeatDates

            const until = this.repeat.endMode === 'until'
                ? appGetUnixTime(this.repeat.until) + 86399
                : null

            const dates = []
            const push = date => {
                const startsAt = appGetUnixTime(date)
                if (until && startsAt > until) return false
                if (startsAt < this.modal.start_at) return true // earlier in the first week

                dates.push({ start: date, startsAt, endsAt: startsAt + duration })
                return true
            }

            // "Alle N Wochen am Mo, Mi" - walk whole weeks, emitting each selected day inside one.
            if (rule.unit === 'week' && rule.weekdays.length) {
                const weekStart = startOfWeek(start, { weekStartsOn: 1 })
                const days = [...rule.weekdays].sort((a, b) => ((a + 6) % 7) - ((b + 6) % 7))

                for (let week = 0; dates.length < limit && week < 260; week += 1) {
                    const cursor = addWeeks(weekStart, week * rule.interval)
                    let exhausted = false

                    for (const weekday of days) {
                        if (dates.length >= limit) break

                        // (weekday + 6) % 7 turns JS's Sunday-first into Monday-first.
                        const date = addDays(cursor, (weekday + 6) % 7)
                        date.setHours(start.getHours(), start.getMinutes(), 0, 0)

                        if (!push(date)) { exhausted = true; break }
                    }

                    if (exhausted) break
                }

                return dates
            }

            // "Alle N Monate am ersten Montag" - the date moves every month, so it is resolved
            // per month rather than generated from the start date.
            if (rule.unit === 'month' && rule.ordinal) {
                const monthStart = new Date(start.getFullYear(), start.getMonth(), 1)

                for (let month = 0; dates.length < limit && month < 120; month += 1) {
                    const cursor = addMonths(monthStart, month * rule.interval)
                    const date = this.nthWeekdayOfMonth(
                        cursor.getFullYear(),
                        cursor.getMonth(),
                        rule.ordinal.weekday,
                        rule.ordinal.position,
                    )

                    if (!date) continue

                    date.setHours(start.getHours(), start.getMinutes(), 0, 0)

                    if (!push(date)) break
                }

                return dates
            }

            // "Alle N Monate am 1., 15." - walk whole months, emitting each selected day inside
            // one. A day the month does not have (31 February) is simply skipped.
            if (rule.unit === 'month' && rule.monthdays.length) {
                const days = [...rule.monthdays].sort((a, b) => a - b)
                const monthStart = new Date(start.getFullYear(), start.getMonth(), 1)

                for (let month = 0; dates.length < limit && month < 120; month += 1) {
                    const cursor = addMonths(monthStart, month * rule.interval)
                    let exhausted = false

                    for (const day of days) {
                        if (dates.length >= limit) break

                        const date = new Date(cursor.getFullYear(), cursor.getMonth(), day)
                        if (date.getMonth() !== cursor.getMonth()) continue

                        date.setHours(start.getHours(), start.getMinutes(), 0, 0)

                        if (!push(date)) { exhausted = true; break }
                    }

                    if (exhausted) break
                }

                return dates
            }

            const step = {
                day: (date, index) => addDays(date, index * rule.interval),
                week: (date, index) => addWeeks(date, index * rule.interval),
                month: (date, index) => addMonths(date, index * rule.interval),
            }[rule.unit]

            if (!step) return []

            for (let index = 0; dates.length < limit && index < 400; index += 1) {
                if (!push(step(start, index))) break
            }

            return dates
        },

        /**
         * The nth $weekday of a month, or null when the month has no such week. position is 1-4 or
         * the string 'last'.
         */
        nthWeekdayOfMonth(year, month, weekday, position) {
            if (position === 'last') {
                // Day 0 of the next month is the last day of this one.
                const date = new Date(year, month + 1, 0)
                date.setDate(date.getDate() - ((date.getDay() - weekday + 7) % 7))

                return date
            }

            const first = new Date(year, month, 1)
            const day = 1 + ((weekday - first.getDay() + 7) % 7) + (position - 1) * 7
            const date = new Date(year, month, day)

            return date.getMonth() === month ? date : null
        },

        toggleMonthday(value) {
            const monthdays = [...this.repeat.monthdays]
            const index = monthdays.indexOf(value)

            if (index === -1) monthdays.push(value)
            else monthdays.splice(index, 1)

            this.repeat.monthdays = monthdays
        },

        openTicketProduct(product) {
            this.$router.push({ name: 'product-main', params: { id: String(product.id) } })
        },
        askDetachTicketProduct(slot, product) {
            this.$refs.detachTicketDialog.open({ slot, product }, {
                message: this.$t('"{product}" will no longer admit to this date; the date takes free registrations again. The product and the tickets already sold are kept.', { product: product.name }),
            })
        },
        detachTicketProduct({ slot, product }) {
            this.eventStore.detachTicketProduct(slot.id, product.id)
                .then(() => {
                    this.$notify({
                        title: this.$t('Success'),
                        message: this.$t('The ticket has been removed from this date.'),
                        type: 'success',
                        iconClass: 'el-icon-fa-check fas',
                        position: 'bottom-left',
                        customClass: 'dark',
                        duration: 4000,
                    })
                    // the Tickets column is part of the event payload - read the event again
                    const id = _.get(this.form, 'id')
                    if (id) this.eventStore.fetchEvent(id)
                })
                .catch(error => {
                    this.$notify({
                        title: this.$t('Error'),
                        message: _.get(error, 'graphQLErrors.0.message') || this.$t('The ticket could not be removed.'),
                        type: 'error',
                        iconClass: 'el-icon-fa-exclamation-triangle fas',
                        position: 'bottom-left',
                        customClass: 'dark',
                        duration: 8000,
                    })
                })
        },
        ticketSaved() {
            // No notification here - the stepper already reports the save, and two stacked toasts
            // saying the same thing is what the merchant sees.
            this.ticketDrawerVisible = false;

            // The Tickets column is part of the event payload, so the list keeps showing the
            // state from before the product existed until the event is read again.
            const id = _.get(this.form, 'id');
            if (id) this.eventStore.fetchEvent(id);
        },
        toggleFilterVisiblity() {
            const visible = this.showFilters

            let value = false
            if (!visible) value = true

            this.disable.showFilters = true
            this.saveFilterVisibility({ scope: this.scope, key: this.key, value })
                .finally(() => this.disable.showFilters = false)
        },
        changeSort(key) {
            let field = this.pagination.sort.field
            let direction = this.pagination.sort.direction;

            if (field === key) {
                // toggle current sort direction
                direction = direction === 'asc' ? 'desc' : 'asc';
            } else {
                field = key;
            }

            this.pagination.sort.field = field;
            this.pagination.sort.direction = direction
        },
        changeFilter(key, value) {
            if (value.match === '' && value.like === '' && value.from === '' && value.to === '' && value.in.length === 0) {
                Vue.delete(this.pagination.filters, key)
            } else {
                Vue.set(this.pagination.filters, key, value)
            }

            this.pagination.pageInfo.currentPage = 1
            this.pagination.pageInfo.total = this.slots.length
            this.pagination.pageInfo.lastPage = Math.ceil(this.slots.length / this.pagination.pageInfo.perPage)
        },
        changeTableColumns(visibleColumns) {

            let activeColumnKeys = []
            this.allColumns.forEach(column => {
                const visible = _.find(visibleColumns, value => column.key === value)
                if (visible) {
                    activeColumnKeys.push({ key: column.key })
                    column.default = true
                } else {
                    column.default = false
                }
            })

            // The setup store's action, not the auth store's setter: updateUserSettings only assigns
            // state.user.settings and never calls the API, so the choice looked applied and was gone
            // on the next load. saveActiveColumnKeys does both, and is what every other list uses.
            this.setupStore.saveActiveColumnKeys({
                scope: this.scope,
                key: this.key,
                data: activeColumnKeys,
            })
        },
        changePaginationSize(value) {
            this.pagination.pageInfo.currentPage = 1
            this.pagination.pageInfo.perPage = value
        },
        changeCurrentPage(value) {
            this.pagination.pageInfo.currentPage = value
        },
        openSlotModal(item = null) {
            this.modalLoading = true
            this.modalVisibility = true

            if (item && item instanceof Event === false) {
                this.modal = _.cloneDeep(item)

                // set default value
                if (!this.modal.hyperlink) {
                    this.modal.hyperlink = {
                        name: '',
                        href: '',
                        type: 'post',
                        target: '_self',
                        download: false,
                        menuItem: undefined,
                        page: undefined,
                    }
                }

                if (item && _.startsWith(item.id, 'new:')) {
                    this.modalType = 'fresh'
                } else {
                    this.modalType = 'existing'
                }
            } else {
                this.modal.id = uniqid('new:')
                this.modalType = 'new'

                // Tomorrow, on the hour, one hour long. The end used to be left empty, which meant
                // picking a whole second date and time for what is almost always "an hour later".
                const startAt = startOfHour(addDays(new Date(), 1))
                this.modal.start_at = appGetUnixTime(startAt)
                this.modal.end_at = appGetUnixTime(addHours(startAt, 1))
            }

            this.findPost()
            this.modalLoading = false
        },
        closeModal() {
            this.modalVisibility = false
            this.modalLoading = false
        },
        submitModalForm() {
            if (this.modalType === 'new') {
                // The repeat end date lives on `repeat`, not on `modal`, so el-form cannot rule it.
                // Without this an empty "on date" silently adds a single slot instead of the series
                // the merchant just described.
                if (this.repeatWarning) return

                this.$refs.modal.validate(valid => {
                    if (valid) {
                        const dates = this.buildRepeatDates()

                        if (dates.length > 1) {
                            // One key across the run, so the list can move or delete it as a unit.
                            const seriesKey = uniqid('series-')

                            dates.forEach(date => {
                                this.addSlot({
                                    ..._.cloneDeep(this.modal),
                                    id: uniqid('new:'),
                                    series_key: seriesKey,
                                    start_at: date.startsAt,
                                    end_at: date.endsAt,
                                })
                            })
                        } else {
                            this.addSlot(this.modal)
                        }

                        this.closeModal()
                    }
                })
            } else {
                const original = (_.get(this.form, 'slots') || []).find(slot => slot.id === this.modal.id)
                const shift = original ? this.modal.start_at - original.start_at : 0
                const siblings = this.seriesSiblings(this.modal)

                // Moving one date of a repeat run is nearly always meant for the whole run - the
                // course shifted by a week, not just its third session.
                if (shift && siblings.length) {
                    this.$confirm(
                        this.$t('This date is part of a repeating series of {count} dates.', { count: siblings.length + 1 }),
                        this.$t('Move date'),
                        {
                            confirmButtonText: this.$t('Move all dates'),
                            cancelButtonText: this.$t('Only this date'),
                            distinguishCancelAndClose: true,
                            type: 'warning',
                        }
                    )
                        .then(() => {
                            this.updateSlot(this.modal)
                            siblings.forEach(sibling => this.updateSlot({
                                ...sibling,
                                start_at: sibling.start_at + shift,
                                end_at: sibling.end_at + shift,
                            }))
                            this.closeModal()
                        })
                        .catch(action => {
                            if (action === 'cancel') {
                                this.updateSlot(this.modal)
                                this.closeModal()
                            }
                        })

                    return
                }

                // replace administrators in team-user
                this.updateSlot(this.modal)
                this.closeModal()
            }
        },
        resetModal() {
            this.modal = {
                id: undefined,
                member_limit: null,
                comment: '',
                hyperlink: {
                    name: '',
                    href: '',
                    type: 'post',
                    target: '_self',
                    download: false,
                    menuItem: undefined,
                    page: undefined,
                },
                ...defaultSlotWindow(),
                // daterange: undefined,
                series_key: null,
                canceled_at: null,
                // TODO
                // address: {
                //     id: undefined,
                //     street: '',
                //     zipcode: '',
                //     city: '',
                //     country: '',
                // },
            }
            this.repeat = {
                pattern: 'none',
                endMode: 'count',
                count: 4,
                until: null,
                customUnit: 'week',
                customInterval: 1,
                weekdays: [],
                monthMode: 'days',
                monthdays: [],
                ordinal: 1,
                ordinalWeekday: 1,
            }

            // NOT resetFields(): Element's FormItem.resetField() broadcasts the field's raw
            // initialValue to every ElTimeSelect below it, which keeps it as userInput - and
            // userInput wins over the bound value until the user picks something. With prop
            // "start_at" that raw value is a unix timestamp, so both time inputs start showing
            // "1786802400" and go on showing it after the modal is reopened. The model is already
            // replaced above, so only the validation state still needs clearing.
            // On nextTick: emptying the model above re-triggers the required rules, and those fire
            // after this call would have run.
            this.$nextTick(() => {
                if (this.$refs.modal) this.$refs.modal.clearValidate()
            })
        },
        disabledStartDate(start) {
            const end = this.modalEndAt
            if (end) return compareAsc(start, end) > 0
            else return false
        },
        disabledEndDate(end) {
            const start = this.modalStartAt
            if (start) return compareDesc(addDays(end, 1), start) > 0
            else return false
        },

        /**
         * A series cannot stop before its first date.
         */
        disabledRepeatUntil(date) {
            if (!this.modal.start_at) return false

            return compareDesc(addDays(date, 1), appFromUnixTime(this.modal.start_at)) > 0
        },

        getPageOptions(target = 'quill') {
            let menus = _.get(this.activeCms, 'config.template.menus', [])
            menus = _.cloneDeep(menus).sort((a, b) => _.get(a, 'position', 0) - _.get(b, 'position', 0))

            const menuOptions = _.get(this.options, 'menu.items') || []
            const visibleOptgroups = menuOptions.filter(el => el.menuItems.length).length > 1

            let tree = []
            menus.forEach(menu => {

                // reduce tree for quill
                if (target === 'quill') {
                    let dir = { folder: menu.name, children: [] }
                    let children = []

                    const menuOption = _.find(menuOptions, { key: menu.key })
                    let items = _.get(menuOption, 'menuItems', [])

                    if (items.length) {
                        items = _.cloneDeep(items)
                        children = NestedSet.createTree(items)
                    }

                    dir.children = children
                    if (children.length) tree.push(dir)
                } else {
                    const menuOption = _.find(menuOptions, { key: menu.key })
                    let items = _.get(menuOption, 'menuItems', [])

                    if (items.length) {
                        items = _.cloneDeep(items)

                        const optgroup = {
                            id: `optgroup-${menuOption.id}`,
                            title: menuOption.name,
                            isDisabled: true,
                            optgroup: true,
                        }

                        if (visibleOptgroups) tree.push(optgroup)
                        tree = [...tree, ...NestedSet.createTree(items)]
                    }
                }
            })

            return tree
        },
        findPost(query = '') {
            let items = []
            if (this.modal.hyperlink.page) items.push(this.modal.hyperlink.page)

            this.post.loading = true
            this.findCategoryPosts(query)
                .then(data => {
                    items = [...items, ...data.items]
                    this.post.items = _.orderBy(_.uniqBy(items, 'id'), 'created_at', 'desc')
                })
                .finally(() => this.post.loading = false)
        },

        /**
         * Siblings generated from the same repeat pattern, minus the one in hand.
         */
        seriesSiblings(slot) {
            const key = _.get(slot, 'series_key')
            if (!key) return []

            return (_.get(this.form, 'slots') || []).filter(
                candidate => candidate.series_key === key && candidate.id !== slot.id && !candidate.deleted
            )
        },

        /**
         * A slot that is part of a repeat run asks the same question a calendar does: just this
         * date, or the whole series?
         */
        deleteSlot(slot) {
            const siblings = this.seriesSiblings(slot)

            if (!siblings.length) {
                this.eventStore.markFormSlotAsDeleted(slot)
                return
            }

            this.$confirm(
                this.$t('This date is part of a repeating series of {count} dates.', { count: siblings.length + 1 }),
                this.$t('Delete date'),
                {
                    confirmButtonText: this.$t('Delete all dates'),
                    cancelButtonText: this.$t('Only this date'),
                    distinguishCancelAndClose: true,
                    type: 'warning',
                }
            )
                .then(() => {
                    this.eventStore.markFormSlotAsDeleted(slot)
                    siblings.forEach(sibling => this.eventStore.markFormSlotAsDeleted(sibling))
                })
                .catch(action => {
                    // 'cancel' is the second button; 'close' is the X and means do nothing.
                    if (action === 'cancel') this.eventStore.markFormSlotAsDeleted(slot)
                })
        },
        restoreSlot(slot) {
            this.eventStore.unmarkFormSlotAsDeleted(slot);
        },
        duplicateSlot(slot) {
            this.eventStore.duplicateFormSlot(slot);
        },
        addSlot(slot) {
            this.eventStore.addFormSlot(slot);
        },
        updateSlot(slot) {
            this.eventStore.updateFormSlot(slot);
        },
        findCategoryPosts(query) {
            return this.eventStore.findCategoryPosts(query);
        },
        saveFilterVisibility(payload) {
            return this.setupStore.saveFilterVisibility(payload);
        },
    },
    mounted() {
        this.findPost()
        this.pagination.pageInfo.total = this.slots.length
        this.pagination.pageInfo.lastPage = Math.ceil(this.slots.length / this.pagination.pageInfo.perPage)
    }
}

</script>

<style lang="sass" scoped>
    .event
        .el-card__body
            & > section.slots
                padding: 0

    .el-form > .el-form-item:last-child
        margin-bottom: 0
    .modal-footer
        display: flex
        justify-content: space-between
        .left
            display: flex
            align-items: center

    // .el-date-editor.el-range-editor
    //     width: 100%
    //     ::v-deep
    //         input.el-range-input
    //             text-align: left
    //             padding-left: 4px

    // One label system for the whole dialog - the admin's own top labels - so every row shares a
    // left edge instead of mixing an inset right-aligned column with left-aligned top labels.
    .ticket-product
        margin-right: .35rem
        cursor: pointer

        i
            margin-right: .35rem

    .no-tickets
        color: #c0c4cc

    .slot-modal-form
        // Page forms breathe; a dialog this dense does not have the room.
        ::v-deep > .el-form-item
            margin-bottom: 12px

            .el-form-item__label
                padding-bottom: 2px
                line-height: 1.4

        .row-value
            display: flex
            align-items: center
            gap: .5rem
            min-width: 0

            .date
                flex: 1
                min-width: 0

            .time
                width: 120px
                flex-shrink: 0

            .seats
                flex: 1
                min-width: 0

            .interval
                width: 92px
                flex-shrink: 0

            .frequency
                width: 140px
                flex-shrink: 0

            .month-mode
                flex-shrink: 0

            .ordinal
                width: 130px
                flex-shrink: 0

            .ordinal-weekday
                width: 150px
                flex-shrink: 0

            .end-mode
                width: 118px
                flex-shrink: 0

            .unit
                color: #606266
                font-size: 13px
                white-space: nowrap

        // Fixed 40px cells rather than flex:1 across the leftover space. Seven flexible cells
        // divide that space fractionally (292/7 = 41.71px), so cell edges land on half device
        // pixels and each one paints 83 or 84px wide - which reads as "different sizes", most
        // visibly on the filled cell. An integer width divides exactly at any pixel ratio.
        .weekday-strip
            display: flex
            flex: 0 0 auto

            ::v-deep .el-checkbox-button
                flex: 0 0 40px

                // 32px, matching the interval input and unit select beside it. Element's small
                // size gets there via padding, so overriding padding to make the cells narrower
                // silently shortens them off the row's control height.
                .el-checkbox-button__inner
                    display: block
                    width: 100%
                    height: 32px
                    padding: 0
                    line-height: 30px
                    font-size: 12px

        // The calendar's 1-31 grid. Seven columns so the columns read as weekday positions do.
        .month-grid
            display: grid
            grid-template-columns: repeat(7, 1fr)
            gap: 4px

            .month-day
                padding: 6px 0
                border: 1px solid #dcdfe6
                border-radius: 4px
                background: #fff
                color: #606266
                font-size: 12px
                cursor: pointer

                &:hover
                    color: #409eff
                    border-color: #c6e2ff

                &.is-active
                    background: #409eff
                    border-color: #409eff
                    color: #fff

        // The generated-dates line sits under End repeat with no label of its own, a little
        // tighter than the row rhythm because it belongs to the row above it.
        .repeat-summary
            margin-top: -4px

        .repeat-preview
            padding: .4rem .625rem
            border-radius: 4px
            background: #f4f4f5
            color: #606266
            font-size: 12px
            line-height: 1.4

            &.is-warning
                background: #fdf6ec
                color: #b88230

    .el-select-dropdown__item
        padding: 0 .5rem
</style>
